let active = null;
let picker = null;
let pickerSeq = 0;

const eligible = (el) => el instanceof HTMLSelectElement && !el.disabled && !el.multiple && el.size <= 1;
const selectFrom = (target) => target instanceof Element ? target.closest('select') : null;

function closePicker(refocus = false) {
  const select = active;
  picker?.remove();
  picker = null;
  active = null;
  if (!select) return;
  select.setAttribute('aria-expanded', 'false');
  select.removeAttribute('aria-controls');
  if (refocus && select.isConnected) select.focus({ preventScroll: true });
}

function positionPicker() {
  if (!active || !picker) return;
  const rect = active.getBoundingClientRect();
  const pad = 8;
  const gap = 4;
  const below = window.innerHeight - rect.bottom - gap - pad;
  const above = rect.top - gap - pad;
  const showBelow = below >= Math.min(240, picker.scrollHeight) || below >= above;
  const room = Math.max(64, showBelow ? below : above);
  picker.style.minWidth = `${Math.ceil(rect.width)}px`;
  picker.style.maxHeight = `${Math.min(352, room)}px`;
  const left = Math.min(Math.max(pad, rect.left), Math.max(pad, window.innerWidth - picker.offsetWidth - pad));
  const height = Math.min(picker.scrollHeight, 352, room);
  picker.style.left = `${Math.round(left)}px`;
  picker.style.top = `${Math.round(showBelow ? rect.bottom + gap : Math.max(pad, rect.top - gap - height))}px`;
}

function choose(index) {
  if (!active) return;
  const select = active;
  const option = select.options[index];
  if (!option || option.disabled || option.parentElement?.disabled) return;
  const changed = select.selectedIndex !== index;
  select.selectedIndex = index;
  closePicker(true);
  if (!changed) return;
  select.dispatchEvent(new Event('input', { bubbles: true }));
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

function addOption(option) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `themed-select-picker-option${option.selected ? ' selected' : ''}`;
  button.textContent = option.textContent.trim();
  button.dataset.index = String(option.index);
  button.setAttribute('role', 'option');
  button.setAttribute('aria-selected', option.selected ? 'true' : 'false');
  button.disabled = option.disabled || !!option.parentElement?.disabled;
  picker.appendChild(button);
}

function openPicker(select) {
  if (!eligible(select)) return;
  if (active === select && picker) return;
  closePicker();
  active = select;
  picker = document.createElement('div');
  picker.id = `themed-select-picker-${++pickerSeq}`;
  picker.className = 'themed-select-picker';
  picker.setAttribute('role', 'listbox');
  picker.setAttribute('aria-label', select.getAttribute('aria-label') || select.labels?.[0]?.textContent.trim() || select.name || 'Options');
  select.setAttribute('aria-haspopup', 'listbox');
  select.setAttribute('aria-expanded', 'true');
  select.setAttribute('aria-controls', picker.id);

  Array.from(select.children).forEach((child) => {
    if (child instanceof HTMLOptGroupElement) {
      const label = document.createElement('div');
      label.className = 'themed-select-picker-group';
      label.textContent = child.label;
      picker.appendChild(label);
      Array.from(child.children).forEach((option) => addOption(option));
    } else if (child instanceof HTMLOptionElement) {
      addOption(child);
    }
  });

  picker.addEventListener('click', (event) => {
    const button = event.target.closest('.themed-select-picker-option');
    if (button && !button.disabled) choose(Number(button.dataset.index));
  });
  document.body.appendChild(picker);
  positionPicker();
  requestAnimationFrame(() => {
    if (!picker) return;
    const selected = picker.querySelector('.themed-select-picker-option.selected:not(:disabled)');
    (selected || picker.querySelector('.themed-select-picker-option:not(:disabled)') || picker).focus({ preventScroll: true });
  });
}

document.addEventListener('pointerdown', (event) => {
  const select = selectFrom(event.target);
  if (eligible(select) && event.button === 0) {
    event.preventDefault();
    select.focus({ preventScroll: true });
    openPicker(select);
    return;
  }
  if (picker && !picker.contains(event.target)) closePicker();
}, true);

document.addEventListener('mousedown', (event) => {
  if (eligible(selectFrom(event.target)) && event.button === 0) event.preventDefault();
}, true);

document.addEventListener('click', (event) => {
  const select = selectFrom(event.target);
  if (!eligible(select)) return;
  event.preventDefault();
  openPicker(select);
}, true);

document.addEventListener('keydown', (event) => {
  const select = selectFrom(event.target);
  if (eligible(select) && ['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
    event.preventDefault();
    openPicker(select);
    return;
  }
  if (!picker || !picker.contains(event.target)) {
    if (event.key === 'Escape') closePicker();
    return;
  }
  const buttons = Array.from(picker.querySelectorAll('.themed-select-picker-option:not(:disabled)'));
  const index = buttons.indexOf(document.activeElement);
  let next = -1;
  if (event.key === 'ArrowDown') next = Math.min(buttons.length - 1, index + 1);
  if (event.key === 'ArrowUp') next = Math.max(0, index - 1);
  if (event.key === 'Home') next = 0;
  if (event.key === 'End') next = buttons.length - 1;
  if (next >= 0) {
    event.preventDefault();
    buttons[next]?.focus({ preventScroll: true });
  } else if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    document.activeElement?.click();
  } else if (event.key === 'Escape') {
    event.preventDefault();
    closePicker(true);
  } else if (event.key === 'Tab') {
    closePicker();
  } else if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
    const start = Math.max(0, index + 1);
    const ordered = buttons.slice(start).concat(buttons.slice(0, start));
    const match = ordered.find((button) => button.textContent.trim().toLowerCase().startsWith(event.key.toLowerCase()));
    if (match) {
      event.preventDefault();
      match.focus({ preventScroll: true });
    }
  }
}, true);

document.addEventListener('change', (event) => {
  if (event.target === active) closePicker();
}, true);
window.addEventListener('resize', () => closePicker());
window.addEventListener('blur', () => closePicker());
window.addEventListener('scroll', (event) => {
  if (picker && !picker.contains(event.target)) closePicker();
}, true);
